import { useAuth } from "@/context/AuthContext";
import { useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, MapPin } from "lucide-react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiError } from "@/lib/api";
import { useOrg } from "@/hooks/useOrg";
import { PageHeader } from "@/components/Badges";
import { TripleSlash } from "@/components/brand/Logo";

const Field = ({ label, value, onChange, testId, type = "text" }) => (
  <div className="space-y-2">
    <Label>{label}</Label>
    <Input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testId} />
  </div>
);

function RegionDialog({ state, onClose, onSaved }) {
  const [form, setForm] = useState(null);
  if (state && !form) setForm(state.region ? { ...state.region, states: state.region.states.join(", ") } : { name: "", code: "", states: "", description: "" });
  if (!state) { if (form) setForm(null); return null; }
  if (!form) return null;
  const save = async () => {
    const payload = { name: form.name, code: form.code, description: form.description, states: form.states.split(",").map((s) => s.trim()).filter(Boolean) };
    try {
      state.region ? await api.patch(`/regions/${state.region.id}`, payload) : await api.post("/regions", payload);
      toast.success("Region saved");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="region-dialog">
        <DialogHeader><DialogTitle className="font-display">{state.region ? "Edit region" : "Add region"}</DialogTitle></DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2"><Field label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testId="region-name-input" /></div>
          <Field label="Code" value={form.code} onChange={(v) => setForm({ ...form, code: v })} testId="region-code-input" />
          <Field label="States (comma separated)" value={form.states} onChange={(v) => setForm({ ...form, states: v })} testId="region-states-input" />
          <div className="sm:col-span-2"><Field label="Description" value={form.description} onChange={(v) => setForm({ ...form, description: v })} testId="region-description-input" /></div>
        </div>
        <DialogFooter>
          <button onClick={onClose} className="rounded-full border border-lasso-line px-5 py-2.5 text-sm font-semibold" data-testid="region-cancel">Cancel</button>
          <button onClick={save} className="rounded-full bg-lasso-ink px-5 py-2.5 text-sm font-semibold text-white hover:bg-lasso-navy" data-testid="region-save">Save</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CommunityDialog({ state, regions, onClose, onSaved }) {
  const [form, setForm] = useState(null);
  if (state && !form) setForm(state.community ? { ...state.community } : { name: "", region_id: state.regionId, city: "", state: "", address: "", homesites: "" });
  if (!state) { if (form) setForm(null); return null; }
  if (!form) return null;
  const save = async () => {
    const payload = { name: form.name, region_id: form.region_id, city: form.city, state: form.state, address: form.address, homesites: form.homesites ? Number(form.homesites) : null };
    try {
      state.community ? await api.patch(`/communities/${state.community.id}`, payload) : await api.post("/communities", payload);
      toast.success("Community saved");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="community-dialog">
        <DialogHeader><DialogTitle className="font-display">{state.community ? "Edit community" : "Add community"}</DialogTitle></DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2"><Field label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testId="community-name-input" /></div>
          <div className="space-y-2 sm:col-span-2">
            <Label>Region</Label>
            <Select value={form.region_id || undefined} onValueChange={(v) => setForm({ ...form, region_id: v })}>
              <SelectTrigger data-testid="community-region-select"><SelectValue placeholder="Choose region" /></SelectTrigger>
              <SelectContent>{regions.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Field label="City" value={form.city} onChange={(v) => setForm({ ...form, city: v })} testId="community-city-input" />
          <Field label="State" value={form.state} onChange={(v) => setForm({ ...form, state: v })} testId="community-state-input" />
          <div className="sm:col-span-2"><Field label="Address" value={form.address} onChange={(v) => setForm({ ...form, address: v })} testId="community-address-input" /></div>
          <Field label="Homesites" type="number" value={form.homesites} onChange={(v) => setForm({ ...form, homesites: v })} testId="community-homesites-input" />
        </div>
        <DialogFooter>
          <button onClick={onClose} className="rounded-full border border-lasso-line px-5 py-2.5 text-sm font-semibold" data-testid="community-cancel">Cancel</button>
          <button onClick={save} className="rounded-full bg-lasso-ink px-5 py-2.5 text-sm font-semibold text-white hover:bg-lasso-navy" data-testid="community-save">Save</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const IconBtn = ({ onClick, icon: Icon, testId, danger }) => (
  <button onClick={onClick} data-testid={testId} className={`rounded-full border border-lasso-line p-2 transition-colors ${danger ? "hover:border-rose-400 hover:text-rose-600" : "hover:border-lasso-ink"}`}>
    <Icon className="h-3.5 w-3.5" />
  </button>
);

export default function OrgStructure() {
  const { user } = useAuth();
  const { regions, communities, reload } = useOrg();
  const [regionState, setRegionState] = useState(null);
  const [communityState, setCommunityState] = useState(null);

  const remove = async (path, label) => {
    if (!window.confirm(`Delete ${label}?`)) return;
    try {
      await api.delete(path);
      toast.success(`${label} deleted`);
      reload();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const saved = () => {
    setRegionState(null);
    setCommunityState(null);
    reload();
  };

  return (
    <div className="fade-up">
      <PageHeader
        eyebrow="Admin"
        title="Regions & communities"
        description="The org structure that drives assignments, regional views and (in Phase 2) progress reporting."
        actions={user.is_super &&
          <button onClick={() => setRegionState({})} className="inline-flex items-center gap-2 rounded-full bg-lasso-ink px-5 py-2.5 text-sm font-semibold text-white hover:bg-lasso-navy" data-testid="admin-add-region-btn">
            <Plus className="h-4 w-4" /> Add region
          </button>
        }
      />
      <div className="space-y-6">
        {regions.map((r) => (
          <section key={r.id} className="rounded-3xl border border-lasso-line bg-white p-6 sm:p-8" data-testid={`region-card-${r.code}`}>
            <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-start">
              <div>
                <TripleSlash />
                <h2 className="mt-4 font-display text-xl font-bold">{r.name}</h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  <span className="font-mono">{r.code}</span> · {r.states.join(", ")} · {r.community_count} communities · {r.user_count} people
                </p>
                {r.description && <p className="mt-2 max-w-xl text-sm text-zinc-600">{r.description}</p>}
              </div>
              <div className="flex gap-2">
                {user.is_super && <button onClick={() => setCommunityState({ regionId: r.id })} className="inline-flex items-center gap-1.5 rounded-full border border-lasso-line px-4 py-2 text-xs font-semibold hover:border-lasso-ink" data-testid={`admin-add-community-btn-${r.code}`}>
                  <Plus className="h-3.5 w-3.5" /> Community
                </button>}
                {user.is_super && <IconBtn onClick={() => setRegionState({ region: r })} icon={Pencil} testId={`region-edit-${r.code}`} />}
                {user.is_super && <IconBtn onClick={() => remove(`/regions/${r.id}`, r.name)} icon={Trash2} testId={`region-delete-${r.code}`} danger />}
              </div>
            </div>
            <div className="mt-6 grid gap-3 md:grid-cols-2">
              {communities.filter((c) => c.region_id === r.id).map((c) => (
                <div key={c.id} className="flex items-center justify-between gap-3 rounded-xl border border-lasso-line bg-lasso-paper px-4 py-3" data-testid={`community-row-${c.id}`}>
                  <div className="min-w-0">
                    <p className="font-semibold">{c.name}</p>
                    <p className="flex items-center gap-1 truncate text-xs text-muted-foreground"><MapPin className="h-3 w-3" />{c.city}, {c.state}{c.homesites ? ` · ${c.homesites} homesites` : ""} · {c.user_count} staff</p>
                  </div>
                  <div className="flex shrink-0 gap-2">
                    {user.is_super && <IconBtn onClick={() => setCommunityState({ community: c })} icon={Pencil} testId={`community-edit-${c.id}`} />}
                    {user.is_super && <IconBtn onClick={() => remove(`/communities/${c.id}`, c.name)} icon={Trash2} testId={`community-delete-${c.id}`} danger />}
                  </div>
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>
      <RegionDialog state={regionState} onClose={() => setRegionState(null)} onSaved={saved} />
      <CommunityDialog state={communityState} regions={regions} onClose={() => setCommunityState(null)} onSaved={saved} />
    </div>
  );
}
